import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { m } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Menu, X, Mail } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import { useLanguage } from "@/contexts/LanguageContext";
import logo from "@/assets/logo.webp";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t } = useLanguage();
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  const navLinks = [
    { href: "#features", label: t("nav.features") },
    { href: "#benefits", label: t("nav.benefits") },
    { href: "#premium", label: t("nav.premium") },
    // { href: "#testimonials", label: t("nav.testimonials") },
    { href: "#download", label: t("nav.waitlist") },
    { href: "#faq", label: t("nav.faq") },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled && !isMobileMenuOpen
          ? "bg-card/95 backdrop-blur-md shadow-soft py-3"
          : isMobileMenuOpen
            ? "bg-transparent py-5"
            : "bg-transparent py-5"
      }`}
    >
      <div className="container flex items-center justify-between relative z-50">
        {/* Logo */}
        <a href="/" className="flex items-center gap-2 group">
          <img
            src={logo}
            alt="FreshKeeper Logo"
            className="w-10 h-10 transition-transform group-hover:scale-105"
            width="40"
            height="40"
            loading="eager"
          />
          <span className="text-xl font-bold text-foreground">FreshKeeper</span>
        </a>

        {/* Desktop Navigation - only shown on homepage */}
        {isHomePage && (
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                {link.label}
              </a>
            ))}
          </nav>
        )}

        {/* Right Side - shown on large screens */}
        <div className="hidden lg:flex items-center gap-3">
          <LanguageSwitcher />
          <ThemeToggle />
          <Button variant="default" size="sm" asChild>
            <a href="#download">
              <Mail className="w-4 h-4" />
              {t("header.waitlist")}
            </a>
          </Button>
        </div>

        {/* Mobile & Tablet Menu Button - shown on screens smaller than lg */}
        <div className="flex lg:hidden items-center gap-2">
          <LanguageSwitcher />
          <ThemeToggle />
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 text-foreground"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu - Full Screen Overlay */}
      {isMobileMenuOpen && (
        <m.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="lg:hidden fixed inset-0 z-40 bg-background/95 backdrop-blur-md pt-24 px-6 overflow-y-auto"
        >
          <nav className="flex flex-col gap-6 items-center text-center">
            {isHomePage &&
              navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-xl font-medium text-foreground py-2 hover:text-primary transition-colors"
                >
                  {link.label}
                </a>
              ))}

            {/* Additional Links for Mobile Menu when not on Home or just general useful links */}
            {!isHomePage && (
              <a
                href="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-xl font-medium text-foreground py-2 hover:text-primary transition-colors"
              >
                {t("nav.home")}
              </a>
            )}

            <div className="flex flex-col gap-4 mt-6 w-full max-w-xs">
              <Button variant="hero" className="w-full justify-center" asChild>
                <a
                  href="#download"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Mail className="w-5 h-5" />
                  {t("header.waitlistJoin")}
                </a>
              </Button>
            </div>
          </nav>
        </m.div>
      )}
    </header>
  );
};

export default Header;
